import React, { useState } from "react";
import { Sparkles, Rocket, CheckCircle2, Circle } from "lucide-react";
import { T, primaryBtn } from "../theme";
import { PillButton } from "./shared";
import { VikiStrategyPanel, VikiCaptionButton } from "./VikiWidgets";

export default function GrowthTab({ profile, derived, actions, staticData, subTab, setSubTab }) {
  const [showAllPlaybook, setShowAllPlaybook] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const { recommended, playbook, categoryScore } = derived;

  return (
    <div data-testid="growth-tab">
      <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
        <PillButton testId="growth-plan-pill" active={subTab === "piano"} onClick={() => setSubTab("piano")} icon={Sparkles}>Il mio piano</PillButton>
        <PillButton testId="growth-campaigns-pill" active={subTab === "campagne"} onClick={() => setSubTab("campagne")} icon={Rocket}>Campagne</PillButton>
      </div>

      {subTab === "piano" && (
        <div>
          <VikiStrategyPanel profileId={profile.id} />
          <div style={{ color: T.textDim, fontSize: 13, marginBottom: 18, maxWidth: 640 }}>
            Queste sono le {recommended.length} azioni più efficaci per l'obiettivo "{profile.goal}". Falle una alla volta,
            senza fretta: contano di più la costanza e la qualità di qualsiasi scorciatoia automatica.
          </div>
          <div data-testid="growth-recommended-plan" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
            <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 10 }}>Piano consigliato per te</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[...recommended].sort((a, b) => {
                const aDone = profile.tasks[`${a[0]}__${a[1]}`] ? 1 : 0;
                const bDone = profile.tasks[`${b[0]}__${b[1]}`] ? 1 : 0;
                if (aDone !== bDone) return aDone - bDone;
                return categoryScore(b[0]) - categoryScore(a[0]);
              }).map(([cat, idx]) => {
                const key = `${cat}__${idx}`;
                const checked = !!profile.tasks[key];
                return (
                  <button data-testid={`growth-task-${cat.replace(/[^a-zA-Z]/g, "")}-${idx}`} key={key} onClick={() => actions.toggleTask(cat, idx)} style={{
                    display: "flex", gap: 8, alignItems: "flex-start", textAlign: "left", background: "transparent",
                    border: "none", cursor: "pointer", padding: 0, color: checked ? T.textDim : T.text, fontSize: 14,
                    lineHeight: 1.4, textDecoration: checked ? "line-through" : "none",
                  }}>
                    {checked ? <CheckCircle2 size={17} color={T.teal} style={{ flexShrink: 0, marginTop: 1 }} /> : <Circle size={17} color={T.textDim} style={{ flexShrink: 0, marginTop: 1 }} />}
                    {playbook.find((c) => c.category === cat).items[idx]}
                  </button>
                );
              })}
            </div>
          </div>
          <button data-testid="growth-show-all-btn" onClick={() => setShowAllPlaybook(!showAllPlaybook)} style={{ background: "transparent", border: "none", color: T.textDim, fontSize: 13, cursor: "pointer", marginBottom: 14, padding: 0 }}>
            {showAllPlaybook ? "Nascondi tutte le tecniche" : "Vedi tutte le tecniche disponibili →"}
          </button>
          {showAllPlaybook && (
            <div className="grid-2" style={{ gap: 16 }}>
              {playbook.map((cat) => (
                <div key={cat.category} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16 }}>
                  <div style={{ fontFamily: T.fontHead, fontSize: 15, marginBottom: 10 }}>{cat.category}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {cat.items.map((item, idx) => {
                      const checked = !!profile.tasks[`${cat.category}__${idx}`];
                      return (
                        <button key={idx} onClick={() => actions.toggleTask(cat.category, idx)} style={{
                          display: "flex", gap: 8, alignItems: "flex-start", textAlign: "left", background: "transparent",
                          border: "none", cursor: "pointer", padding: 0, color: checked ? T.textDim : T.text, fontSize: 13,
                          lineHeight: 1.4, textDecoration: checked ? "line-through" : "none",
                        }}>
                          {checked ? <CheckCircle2 size={16} color={T.teal} style={{ flexShrink: 0, marginTop: 1 }} /> : <Circle size={16} color={T.textDim} style={{ flexShrink: 0, marginTop: 1 }} />}
                          {item}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === "campagne" && (
        <div data-testid="growth-campaigns-list">
          <div style={{ color: T.textDim, fontSize: 13, marginBottom: 18, maxWidth: 640 }}>
            Modelli pronti, basati su ciò che oggi funziona meglio su Instagram. Scegline uno:
            verrà applicato al calendario settimanale in un clic. Tocca il titolo per vedere i dettagli.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[...staticData.campaigns].sort((a, b) => {
              const aRec = a.id === staticData.recommendedCampaignByGoal[profile.goal] ? -1 : 0;
              const bRec = b.id === staticData.recommendedCampaignByGoal[profile.goal] ? -1 : 0;
              return aRec - bRec;
            }).map((c) => {
              const isRecommended = c.id === staticData.recommendedCampaignByGoal[profile.goal];
              const isOpen = expandedCampaign === c.id;
              return (
                <div key={c.id} data-testid={`campaign-card-${c.id}`} style={{ background: T.surface, border: `1px solid ${isRecommended ? T.amber : T.line}`, borderRadius: 10, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button data-testid={`campaign-toggle-${c.id}`} onClick={() => setExpandedCampaign(isOpen ? null : c.id)} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, background: "transparent", border: "none", cursor: "pointer", padding: 0, textAlign: "left", flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontFamily: T.fontHead, fontSize: 15 }}>{c.title}</span>
                        {isRecommended && <span data-testid="campaign-recommended-badge" style={{ fontSize: 10, fontFamily: T.fontHead, color: T.ink, background: T.amber, borderRadius: 20, padding: "2px 8px" }}>Consigliata per te</span>}
                      </div>
                      <div style={{ fontSize: 12, color: T.textDim }}>{c.duration} · {isOpen ? "tocca per richiudere" : "tocca per i dettagli"}</div>
                    </button>
                    <button data-testid={`campaign-apply-${c.id}`} onClick={() => actions.applyCampaign(c.id)} style={{ ...primaryBtn, flexShrink: 0 }}>Applica al calendario</button>
                  </div>
                  {isOpen && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line}` }}>
                      <div style={{ fontSize: 13, color: T.textDim, marginBottom: 14, lineHeight: 1.5, maxWidth: 560 }}>{c.rationale}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {c.steps.map((s, i) => (
                          <div key={i}>
                            <div style={{ fontSize: 13, display: "flex", gap: 10, alignItems: "baseline" }}>
                              <span style={{ color: T.amber, fontFamily: T.fontHead, fontSize: 11, minWidth: 32 }}>{s.day}</span>
                              <span style={{ color: T.textDim, fontSize: 11, minWidth: 58 }}>{s.format}</span>
                              <span>{s.text}</span>
                            </div>
                            {s.format !== "Azione" && (
                              <div style={{ marginTop: 4, marginLeft: 42 }}>
                                <VikiCaptionButton ideaText={s.text} profileId={profile.id} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
